import type { SeriesName, SeriesPoint } from "@/api/recordsSeries";
import type { ChartSpec } from "@/components/chartLines";
import { PIPELINE_COLORS, SERIES_COLORS } from "@/components/seriesPalette";
import { momentLabel } from "@/utils/moments";

// The slices a pie draws (TTTL-304), read from each pipeline's series at
// the last moment watched: the reading a report for that moment would
// make. One pipeline splits its records watched — the denominator the
// engine reads dropped and incomplete against — into records dropped,
// records incomplete and the rest, which the engine counts nowhere and
// names nowhere: "records kept" is this chart's word for watched and
// neither dropped nor incomplete. The keys read as the lines' do, in the
// Figma hues of the three lines. A comparison gives
// each pipeline a slice of one count, in the overlay palette by its
// place in the fence; a pipeline nobody watched in the window is named
// rather than drawn, and never shifts the others' hues.

export interface ChartSlice {
  label: string;
  color: string;
  value: number;
}

export interface ShareData {
  /** The latest moment any slice was read at. */
  at: string;
  slices: ChartSlice[];
  /** Pipelines whose series came but held no watched count. */
  unread: string[];
}

/** The last moment a pipeline's series knew its watched count, and the
 *  three counts there; an uncounted dropped or incomplete line is nothing
 *  dropped, nothing incomplete — the engine leaves them null on a run
 *  recorded before outcomes were counted, not when a count is unknown. */
function lastReading(
  points: SeriesPoint[],
):
  | { at: string; watched: number; dropped: number; incomplete: number }
  | undefined {
  for (let i = points.length - 1; i >= 0; i--) {
    const p = points[i];
    if (p?.watched !== null && p?.watched !== undefined) {
      return {
        at: p.at,
        watched: p.watched,
        dropped: p.dropped ?? 0,
        incomplete: p.incomplete ?? 0,
      };
    }
  }
  return undefined;
}

export function chartSlices(
  chart: ChartSpec,
  fetched: ReadonlyMap<string, SeriesPoint[]>,
): ShareData | undefined {
  if (chart.pipelines.length === 1) {
    const only = chart.pipelines[0] ?? "";
    const reading = lastReading(fetched.get(only) ?? []);
    if (!reading) return undefined;
    const { at, watched, dropped, incomplete } = reading;
    return {
      at,
      unread: [],
      slices: [
        {
          label: "records kept",
          color: SERIES_COLORS.watched,
          value: Math.max(0, watched - dropped - incomplete),
        },
        {
          label: "records dropped",
          color: SERIES_COLORS.dropped,
          value: dropped,
        },
        {
          label: "records incomplete",
          color: SERIES_COLORS.incomplete,
          value: incomplete,
        },
      ],
    };
  }
  const count: SeriesName = chart.series[0] ?? "watched";
  const slices: ChartSlice[] = [];
  const unread: string[] = [];
  let at: string | undefined;
  chart.pipelines.forEach((pipeline, slot) => {
    const points = fetched.get(pipeline);
    if (points === undefined) return;
    const reading = lastReading(points);
    if (!reading) {
      unread.push(pipeline);
      return;
    }
    if (at === undefined || reading.at > at) at = reading.at;
    slices.push({
      label: pipeline,
      color: PIPELINE_COLORS[slot] ?? PIPELINE_COLORS[0],
      value: reading[count],
    });
  });
  if (at === undefined) return undefined;
  return { at, slices, unread };
}

/** Whether there is anything to draw: false when every slice is zero. */
export function hasShares(slices: ChartSlice[]): boolean {
  return slices.some((s) => s.value > 0);
}

/** A slice's share of the whole, as a whole percentage. */
export function percentOf(value: number, total: number): number {
  return total > 0 ? Math.round((value / total) * 100) : 0;
}

/** The pie in words, for a reader who cannot see the canvas: the moment
 *  and each slice's count and share. */
export function describeShares({ at, slices }: ShareData): string {
  const total = slices.reduce((sum, s) => sum + s.value, 0);
  const parts = slices.map(
    (s) => `${s.label} ${s.value} (${percentOf(s.value, total)}%)`,
  );
  return `Share of records at ${momentLabel(at)}: ${parts.join(", ")}.`;
}
